import { SagaIterator } from 'redux-saga'
import { Action } from 'redux-actions'
import { put, takeLatest, select, all, take } from 'redux-saga/effects'

import { getFormTitle } from '@tabeeb/modules/appConfigState/selectors'
import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { getIsCurrentUserReviewer } from '@tabeeb/modules/../users/selectors'
import { onAddInfoNotification, onAddErrorNotification } from '@tabeeb/modules/notification/actions'

import * as rawActions from '../actions'
import { signalrEvents, signalrActions, signalrConstants } from '../../signalr'
import { getContentForms, getCurrentForm } from '../selectors'

function* attachFormSuccess(action: ReturnType<typeof rawActions.attachForm.success>): SagaIterator {
  const attachModel = action.payload
  const formId = attachModel.FormId
  const contentId: ReturnType<typeof getContentId> = yield select(getContentId)
  const userId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)
  const formTitle: ReturnType<typeof getFormTitle> = yield select(getFormTitle)

  yield put(onAddInfoNotification({ message: `${formTitle} attached to session successfully` }))
  yield put(signalrActions.invokeHubAction({ method: 'AttachForm', args: [contentId, formId] }))
  yield put(rawActions.setLastAttachedForm({ formId, isAttachedFromCondition: !!attachModel.IsAttachedFromCondition }))
  yield put(
    rawActions.getContentForm.request({
      ...attachModel,
      UserId: userId,
    })
  )
}

function* formAttached(
  action: Action<[{ formId: number; userId: number; showSwitchFormDialog: boolean }]>
): SagaIterator {
  const [{ formId, userId, showSwitchFormDialog }] = action.payload

  const isReviewer: ReturnType<typeof getIsCurrentUserReviewer> = yield select(getIsCurrentUserReviewer)
  if (isReviewer) {
    return
  }

  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)
  const contentId: ReturnType<typeof getContentId> = yield select(getContentId)
  const formTitle: ReturnType<typeof getFormTitle> = yield select(getFormTitle)

  const isAttachedFromCondition = showSwitchFormDialog && currentUserId === userId

  yield put(onAddInfoNotification({ message: `${formTitle} attached to session successfully` }))
  yield put(rawActions.setLastAttachedForm({ formId, isAttachedFromCondition }))
  yield put(rawActions.getContentForm.request({ ContentId: contentId, FormId: formId, UserId: userId }))
}

function* formDetached(action: Action<[unknown, number, number]>): SagaIterator {
  const [, userId, formId] = action.payload
  if (!formId) {
    return
  }

  const currentUserId: ReturnType<typeof getCurrentUserId> = yield select(getCurrentUserId)
  if (currentUserId === userId) {
    return
  }

  yield put(rawActions.removeContentForm(formId))

  const formTitle: ReturnType<typeof getFormTitle> = yield select(getFormTitle)

  yield put(onAddInfoNotification({ message: `${formTitle} detached from session successfully` }))
  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const contentForms: ReturnType<typeof getContentForms> = yield select(getContentForms)

  if (currentForm.FormId === formId && contentForms.length > 0) {
    yield put(rawActions.getFormControls.request({ contentFormId: contentForms[0].Id }))
  }

  if (contentForms.length === 0) {
    yield put(rawActions.unsetCurrentForm())
  }
}

function* formUpdated(action: Action<[unknown, number]>): SagaIterator {
  const formId = action.payload[1]
  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const contentForms: ReturnType<typeof getContentForms> = yield select(getContentForms)

  const updatedForm = contentForms.find((form) => form.Id === formId)

  if (!updatedForm) return

  if (currentForm && currentForm.Id === formId) {
    yield put(rawActions.updateReattachCount())
    yield put(rawActions.getFormControls.request({ contentFormId: updatedForm.Id }))

    const result = yield take([rawActions.getFormControls.success, rawActions.getFormControls.failed])
    if (result.type === rawActions.getFormControls.success({ contentFormId: updatedForm.Id }).type) {
      const formTitle: ReturnType<typeof getFormTitle> = yield select(getFormTitle)
      yield put(onAddInfoNotification({ message: `'${updatedForm.Name}' ${formTitle} has been updated` }))
    }
  }

  updatedForm.ReattachedNumberOfTimes += 1
}

function* handleDetachFormFailed({ response }: ReturnType<typeof rawActions.detachForm.failed>): SagaIterator {
  yield put(onAddErrorNotification({ message: response.data?.Error?.Message || 'Failed to detach form' }))
}

function* handleDetachFormSuccess({
  payload: { formId },
}: ReturnType<typeof rawActions.detachForm.success>): SagaIterator {
  const formTitle: ReturnType<typeof getFormTitle> = yield select(getFormTitle)

  yield put(onAddInfoNotification({ message: `${formTitle} detached from session successfully` }))

  const contentId: ReturnType<typeof getContentId> = yield select(getContentId)
  yield put(signalrActions.invokeHubAction({ method: 'DetachForm', args: [contentId, formId] }))

  yield put(rawActions.removeContentForm(formId))

  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)
  const contentForms: ReturnType<typeof getContentForms> = yield select(getContentForms)

  if (currentForm.FormId === formId && contentForms.length > 0) {
    yield put(rawActions.getFormControls.request({ contentFormId: contentForms[0].Id }))
  }

  if (contentForms.length === 0) {
    yield put(rawActions.unsetCurrentForm())
  }
}

function* attachFormFailed({ response }: ReturnType<typeof rawActions.attachForm.failed>): SagaIterator {
  yield put(onAddErrorNotification({ message: response.data?.Error?.Message ?? 'Failed to attach form' }))
}

function* detachCurrentForm(): SagaIterator {
  const contentId: ReturnType<typeof getContentId> = yield select(getContentId)
  const currentForm: ReturnType<typeof getCurrentForm> = yield select(getCurrentForm)

  yield put(rawActions.detachForm.request({ contentId, formId: currentForm.FormId! }))
}

function* attachDetachForm() {
  yield all([
    takeLatest(rawActions.attachForm.success, attachFormSuccess),
    takeLatest(rawActions.attachForm.failed, attachFormFailed),
    takeLatest(rawActions.detachForm.success, handleDetachFormSuccess),
    takeLatest(rawActions.detachForm.failed, handleDetachFormFailed),
    takeLatest(rawActions.detachCurrentForm, detachCurrentForm),
    // signalr
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onFormAttached, formAttached),
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onFormDetached, formDetached),
    takeLatest(signalrEvents[signalrConstants.tabeebHubName].onFormUpdated, formUpdated),
  ])
}

export default attachDetachForm
